import RingIcon from '../../../../../../../assets/svgs/Ring'

import EventItem from './EventItem'
import SeeAllOnChimp from '../SeeAllOnChimp'
import style from './style.module.css'

const SKELETON_ROWS = 4

/**
 * The events panel.
 *
 * The API hands us events already ordered upcoming-first (soonest first) then
 * past (most recent first), with an accurate `isUpcoming` per row. That ordering
 * is only useful if the UI honours it: a company whose last event was months ago
 * used to be announced as "Happening at …", which is the one thing this block
 * must never claim. So the heading follows the data, and when both groups are
 * present an explicit divider marks where the archive starts.
 */
const EventsBlock = ({ company, events = [], loading = false, error = null, onRetry }) => {
  const list = Array.isArray(events) ? events : []
  const isEmpty = !loading && !error && list.length === 0

  // filter, not sort: the server's ordering inside each group is deliberate.
  const upcoming = list.filter((event) => event.isUpcoming)
  const past = list.filter((event) => !event.isUpcoming)

  // "Recently at" only once we know there is nothing ahead. While loading, and
  // when there is nothing at all, the neutral "Happening at" is still correct.
  const isArchive = past.length > 0 && upcoming.length === 0
  const heading = isArchive ? 'Recently at' : 'Happening at'

  const initials =
    company?.initials || String(company?.name || '?').trim().slice(0, 2).toUpperCase()

  const renderItem = (event) => (
    <EventItem key={event.id} event={event} initials={initials} orgKey={company?.orgKey} />
  )

  return (
    <div className={style.companyModal_events}>
      <div className={style.companyModal_events_title}>
        <RingIcon />
        <span>
          {heading} {company?.name}
        </span>
      </div>

      <div className={style.companyModal_events_list} aria-busy={loading}>
        {loading &&
          Array.from({ length: SKELETON_ROWS }).map((item, index) => (
            <div
              // Skeletons carry no identity of their own, so the index is the key.
              key={`event-skeleton-${index}`}
              className={style.companyModal_events_skeleton}
              aria-hidden="true"
            >
              <span className={style.companyModal_events_skeleton_thumb} />
              <span className={style.companyModal_events_skeleton_lines}>
                <span />
                <span />
              </span>
            </div>
          ))}

        {!loading && error && (
          <div className={style.companyModal_events_message} role="status">
            <span>Could not load events.</span>
            <button
              type="button"
              className={style.companyModal_events_retry}
              onClick={onRetry}
              data-log-action="spotlight_retry_company"
            >
              Retry
            </button>
          </div>
        )}

        {isEmpty && (
          <div className={style.companyModal_events_message}>
            <span>No events scheduled</span>
          </div>
        )}

        {!loading && !error && upcoming.map(renderItem)}

        {!loading && !error && upcoming.length > 0 && past.length > 0 && (
          <div className={style.companyModal_events_divider} role="separator">
            <span>Past events</span>
          </div>
        )}

        {!loading && !error && past.map(renderItem)}
      </div>

      {/* Always offered, not only when the list overflows: the panel holds a
          sample either way, so there is always more on Chimp than fits here. */}
      {!loading && !error && events.length > 0 && (
        <SeeAllOnChimp companyName={company?.name} label="See all events on Chimp" />
      )}
    </div>
  )
}

export default EventsBlock
